import React, { forwardRef, useCallback, useEffect, useRef, useState, type ComponentPropsWithoutRef } from "react";

import { createPortal } from "react-dom";
import {
  MiniMap as MiniMapRZP,
  TransformComponent,
  TransformWrapper,
  useControls,
  useTransformContext,
  useTransformEffect,
  type MiniMapProps as MiniMapPropsRZP,
  type ReactZoomPanPinchContentRef,
  type ReactZoomPanPinchProps,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";

import { CompassIcon, MinusIcon, PlanIcon, PlusIcon } from "@eisox/icons";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";
import { Slot } from "@radix-ui/react-slot";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { cn } from "..";
import {
  useClickOnPlan,
  useCompassHeading,
  useDragItem,
  useRectangleSelection,
  useResponsivePlan,
  type CompassStatus,
} from "./hooks";
import type { SelectableItemEntry } from "./hooks/useRectangleSelection";
import { planRotation, type Point } from "./utils";

/* -------------------------------------------------------------------------------------------------
 * Plan
 * -----------------------------------------------------------------------------------------------*/

const PLAN_NAME = "Plan";

interface ImageProperties {
  width: number;
  height: number;
  src: string;
}

interface PlanContextValue {
  imageProperties: ImageProperties | null;
  setImageProperties: React.Dispatch<React.SetStateAction<ImageProperties | null>>;
  rectangleSelection: boolean;
  onRectangleSelect?: (itemIds: string[]) => void;
  registerSelectableItem: (entry: SelectableItemEntry) => void;
  unregisterSelectableItem: (id: string) => void;
  getSelectableItems: () => SelectableItemEntry[];
  isDraggingRectangle: boolean;
  setIsDraggingRectangle: (isDragging: boolean) => void;
  setWrapperElement: (element: HTMLDivElement | null) => void;
  wrapperElement: HTMLDivElement | null;
  rotated: boolean;
  getRotation: () => number;
  setNorthAngle: (northAngle: number | undefined) => void;
  compassAvailable: boolean;
  compassStatus: CompassStatus;
  toggleCompass: VoidFunction;
}

const ROTATION_VARIABLE = "--plan-rotation";
const TOUCH_DEVICE_QUERY = "(pointer: coarse)";

type CompassError = "denied" | "unavailable";

const [PlanContextProvider, usePlanContext] = createContext<PlanContextValue>(PLAN_NAME);

interface RootProps extends Omit<ReactZoomPanPinchProps, "centerOnInit"> {
  onClickOnPlan?: (point: Point) => void;
  rectangleSelection?: boolean;
  onRectangleSelect?: (itemIds: string[]) => void;
}

const Root = forwardRef<ReactZoomPanPinchRef, RootProps>(
  (
    {
      onClickOnPlan,
      rectangleSelection = false,
      onRectangleSelect,
      panning: panningProp,
      minScale,
      limitToBounds,
      ...props
    },
    forwardedRef,
  ) => {
    const [imageProperties, setImageProperties] = useState<ImageProperties | null>(null);
    const [isDraggingRectangle, setIsDraggingRectangle] = useState(false);
    const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);
    const [northAngle, setNorthAngle] = useState<number>();
    const [isTouchDevice] = useState(() => "matchMedia" in window && window.matchMedia(TOUCH_DEVICE_QUERY).matches);
    const compass = useCompassHeading();
    const rotationRef = useRef(0);

    const rotation =
      northAngle === undefined || compass.heading === undefined ? undefined : planRotation(northAngle, compass.heading);
    const rotated = rotation !== undefined;

    const selectableItemsRef = useRef(new Map<string, SelectableItemEntry>());

    const registerSelectableItem = useCallback((entry: SelectableItemEntry) => {
      selectableItemsRef.current.set(entry.id, entry);
    }, []);

    const unregisterSelectableItem = useCallback((id: string) => {
      selectableItemsRef.current.delete(id);
    }, []);

    const getSelectableItems = useCallback(() => Array.from(selectableItemsRef.current.values()), []);

    const getRotation = useCallback(() => rotationRef.current, []);

    const { handleMouseDown, handleMouseUp } = useClickOnPlan({ onClickOnPlan, getRotation });

    const { status: compassStatus, start: startCompass, stop: stopCompass } = compass;

    useEffect(() => {
      if (northAngle === undefined) stopCompass();
    }, [northAngle, stopCompass]);

    useEffect(() => {
      rotationRef.current = rotation ?? 0;
      if (!wrapperElement || rotation === undefined) return;
      wrapperElement.style.setProperty(ROTATION_VARIABLE, `${rotation}deg`);
      return () => {
        wrapperElement.style.removeProperty(ROTATION_VARIABLE);
      };
    }, [rotation, wrapperElement]);

    const compassActive = compassStatus === "active";

    const ref = useRef<ReactZoomPanPinchRef>(null);

    const composedRefs = useComposedRefs(forwardedRef, ref);

    return (
      <PlanContextProvider
        imageProperties={imageProperties}
        setImageProperties={setImageProperties}
        rectangleSelection={rectangleSelection}
        onRectangleSelect={onRectangleSelect}
        registerSelectableItem={registerSelectableItem}
        unregisterSelectableItem={unregisterSelectableItem}
        getSelectableItems={getSelectableItems}
        isDraggingRectangle={isDraggingRectangle}
        setIsDraggingRectangle={setIsDraggingRectangle}
        setWrapperElement={setWrapperElement}
        wrapperElement={wrapperElement}
        rotated={rotated}
        getRotation={getRotation}
        setNorthAngle={setNorthAngle}
        compassAvailable={northAngle !== undefined && isTouchDevice}
        compassStatus={compassStatus}
        toggleCompass={() => {
          if (compassActive) stopCompass();
          else void startCompass();
        }}
      >
        <TransformWrapper
          {...props}
          limitToBounds={rotated ? false : limitToBounds}
          minScale={(minScale ?? 1) * 0.8}
          ref={composedRefs}
          panning={{
            ...panningProp,
            disabled: isDraggingRectangle || panningProp?.disabled,
          }}
          onPanningStart={(rzppRef, event) => {
            if (rectangleSelection && event.shiftKey) {
              return;
            }
            handleMouseDown(rzppRef, event);
            props.onPanningStart?.(rzppRef, event);
          }}
          onPanningStop={(rzppRef, event) => {
            handleMouseUp(rzppRef, event);
            props.onPanningStop?.(rzppRef, event);
          }}
          centerOnInit
        />
      </PlanContextProvider>
    );
  },
);

Root.displayName = PLAN_NAME;

/* -------------------------------------------------------------------------------------------------
 * Wrapper
 * -----------------------------------------------------------------------------------------------*/

const WRAPPER_NAME = "PlanWrapper";

interface WrapperProps extends ComponentPropsWithoutRef<"div"> {
  plan?: File;
  onPlanLoaded?: VoidFunction;
  /**
   * Clockwise rotation, in degrees, that puts north at the top of the plan. On touch devices `Plan.Controls` then
   * shows a compass button turning the plan with the phone heading; items counter-rotate to stay upright.
   */
  northAngle?: number;
  onCompassError?: (error: CompassError) => void;
}

const Wrapper: React.FC<WrapperProps> = props => {
  const {
    plan,
    onPlanLoaded,
    northAngle,
    onCompassError,
    className,
    style,
    children,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    ...wrapperProps
  } = props;

  const {
    imageProperties,
    setImageProperties,
    rectangleSelection,
    onRectangleSelect,
    getSelectableItems,
    setIsDraggingRectangle,
    setWrapperElement,
    wrapperElement,
    rotated,
    setNorthAngle,
    compassStatus,
  } = usePlanContext("PlanWrapper");
  const instance = useTransformContext();
  const { updateDimensions } = useResponsivePlan(plan);

  const { isDragging, rectangle, handleMouseDown, handleMouseMove, handleMouseUp } = useRectangleSelection({
    enabled: rectangleSelection,
    container: wrapperElement,
    getSelectableItems,
    onRectangleSelect,
    onDraggingChange: setIsDraggingRectangle,
  });

  const [loading, setLoading] = useState(true);
  const prevPlan = useRef(plan);

  useEffect(() => {
    setNorthAngle(northAngle);
    return () => setNorthAngle(undefined);
  }, [northAngle, setNorthAngle]);

  useEffect(() => {
    if (compassStatus === "denied" || compassStatus === "unavailable") onCompassError?.(compassStatus);
  }, [compassStatus, onCompassError]);

  useEffect(() => {
    setWrapperElement(instance.wrapperComponent ?? null);
  }, [instance.wrapperComponent, setWrapperElement]);

  useEffect(() => {
    if (!plan || !instance.wrapperComponent) return;

    setLoading(true);
    prevPlan.current = plan;

    const { width, height } = instance.wrapperComponent.getBoundingClientRect();

    void updateDimensions(width, height).then(dimensions => {
      const url = URL.createObjectURL(plan);

      setImageProperties({ src: url, ...dimensions });

      instance.getContext().resetTransform(0);
      instance.getContext().centerView(undefined, 0);
      setLoading(false);

      return () => URL.revokeObjectURL(url);
    });

    // oxlint-disable-next-line react/exhaustive-deps
  }, [plan]);

  const wrapperRect = wrapperElement?.getBoundingClientRect();
  const selectionOverlay =
    isDragging && wrapperRect
      ? createPortal(
          <div
            className="border-black/50 bg-black/10 pointer-events-none fixed z-[1301] border-2 border-dashed"
            style={{
              left: wrapperRect.left + Math.min(rectangle.startX, rectangle.endX),
              top: wrapperRect.top + Math.min(rectangle.startY, rectangle.endY),
              width: Math.abs(rectangle.endX - rectangle.startX),
              height: Math.abs(rectangle.endY - rectangle.startY),
            }}
          />,
          document.body,
        )
      : null;

  return (
    <>
      <TransformComponent
        wrapperClass={cn(
          "overflow-hidden rounded-[10px] border border-primary-darker-gray bg-primary-white",
          className,
        )}
        wrapperStyle={style}
        wrapperProps={{
          ...wrapperProps,
          onMouseDown: e => {
            handleMouseDown(e);
            onMouseDown?.(e);
          },
          onMouseMove: e => {
            handleMouseMove(e);
            onMouseMove?.(e);
          },
          onMouseUp: e => {
            handleMouseUp();
            onMouseUp?.(e);
          },
        }}
        contentStyle={{ position: "relative" }}
      >
        {!loading && plan === prevPlan.current && imageProperties && (
          <div
            data-plan-layer=""
            style={{ position: "relative", transform: rotated ? `rotate(var(${ROTATION_VARIABLE}))` : undefined }}
          >
            {children}
            <img
              alt="plan"
              src={imageProperties.src}
              style={{
                display: "block",
                width: `${imageProperties.width}px`,
                height: `${imageProperties.height}px`,
              }}
              onLoad={() => onPlanLoaded?.()}
            />
          </div>
        )}
      </TransformComponent>
      {selectionOverlay}
    </>
  );
};

Wrapper.displayName = WRAPPER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

interface ItemProps extends React.ComponentPropsWithRef<"div"> {
  position?: Point;
  origin?: "center" | "bottom center";
  asChild?: boolean;
  onPositionChange?: (point: Point) => void;
  onPositionEnd?: (point: Point) => void;
  defaultPosition?: Point;
  draggable?: boolean;
  selectionId?: string;
  selectionDisabled?: boolean;
}

const Item = forwardRef<HTMLDivElement, ItemProps>((props, forwardedRef) => {
  const {
    position: positionProp,
    onPositionChange,
    onPositionEnd,
    defaultPosition = { x: 0, y: 0 },
    draggable = false,
    origin = "center",
    selectionId,
    selectionDisabled = false,
    style,
    asChild,
    children,
    ...itemProps
  } = props;

  const { registerSelectableItem, unregisterSelectableItem, rotated, getRotation } = usePlanContext("PlanItem");
  const instance = useTransformContext();

  const Comp = asChild ? Slot : "div";

  const [scale, setScale] = useState<number>(instance.getContext().state.scale);
  const [position, setPosition] = useControllableState<Point>({
    prop: positionProp,
    onChange: onPositionChange,
    defaultProp: defaultPosition,
  });

  const { target } = useDragItem<HTMLDivElement>({
    origin,
    getRotation,
    onDragStart: instance.clearPanning,
    onDragChange: ({ x, y }) => setPosition({ x, y }),
    onDragEnd: (_, point) => onPositionEnd?.(point),
    disabled: !draggable,
  });

  const ref = useRef<HTMLDivElement>(null);

  const registerRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      if (!selectionId) return;

      if (node) {
        registerSelectableItem({ id: selectionId, element: node, disabled: selectionDisabled });
      } else {
        unregisterSelectableItem(selectionId);
      }
    },
    [registerSelectableItem, selectionDisabled, selectionId, unregisterSelectableItem],
  );

  const composedRefs = useComposedRefs(registerRef, forwardedRef, target);

  useTransformEffect(({ state }) => {
    setScale(state.scale);
  });

  useEffect(() => {
    if (!selectionId || !ref.current) return;
    registerSelectableItem({ id: selectionId, element: ref.current, disabled: selectionDisabled });
  }, [registerSelectableItem, selectionDisabled, selectionId]);

  return (
    <Comp
      {...itemProps}
      data-plan-item=""
      ref={composedRefs}
      style={{
        ...style,
        position: "absolute",
        left: `${position.x}%`,
        top: `${position.y}%`,
        userSelect: "none",
        transformOrigin: origin === "center" ? "center" : "bottom center",
        transform: `translate(-50%, -${origin === "center" ? "50%" : "100%"}) scale(${1 / scale})${rotated ? ` rotate(calc(var(${ROTATION_VARIABLE}) * -1))` : ""}`,
      }}
    >
      {children}
    </Comp>
  );
});

Item.displayName = "PlanItem";

/* -------------------------------------------------------------------------------------------------
 * Controls
 * -----------------------------------------------------------------------------------------------*/

const CONTROL_BUTTON_NAME = "ControlButton";

type ControlButtonProps = React.ComponentPropsWithoutRef<"button">;

const ControlButton = forwardRef<HTMLButtonElement, ControlButtonProps>(({ className, ...props }, forwardedRef) => (
  <button
    type="button"
    {...props}
    ref={forwardedRef}
    className={cn(
      "h-8 w-8 [&>svg]:h-2.5 [&>svg]:w-2.5 flex items-center justify-center rounded-[6px] border-none bg-primary-gray text-primary-dark-gray",
      className,
    )}
  />
));

ControlButton.displayName = CONTROL_BUTTON_NAME;

const CONTROLS_NAME = "Controls";

type ControlsProps = React.ComponentPropsWithoutRef<"div">;

const Controls = forwardRef<HTMLDivElement, ControlsProps>(({ className, children, ...props }, forwardedRef) => {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  const { compassAvailable, compassStatus, toggleCompass } = usePlanContext(CONTROLS_NAME);
  const compassActive = compassStatus === "active";

  return (
    <div {...props} ref={forwardedRef} className={cn("flex flex-col gap-[5px]", className)}>
      <ControlButton
        onClick={e => {
          e.preventDefault();
          zoomIn();
        }}
      >
        <PlusIcon />
      </ControlButton>
      <ControlButton
        onClick={e => {
          e.preventDefault();
          zoomOut();
        }}
      >
        <MinusIcon />
      </ControlButton>
      <ControlButton
        onClick={e => {
          e.preventDefault();
          resetTransform();
        }}
      >
        <PlanIcon />
      </ControlButton>
      {compassAvailable && (
        <ControlButton
          aria-pressed={compassActive}
          className={cn(compassActive && "bg-primary-blue text-primary-white")}
          onClick={e => {
            e.preventDefault();
            toggleCompass();
          }}
        >
          <CompassIcon />
        </ControlButton>
      )}
      {children}
    </div>
  );
});

Controls.displayName = CONTROLS_NAME;

/* -------------------------------------------------------------------------------------------------
 * Minimap
 * ------------------------------------------------------------------------------------------------*/

type MiniMapProps = Omit<MiniMapPropsRZP, "children">;

const MiniMap: React.FC<MiniMapProps> = props => {
  const { imageProperties } = usePlanContext("minimap");
  return imageProperties ? (
    <MiniMapRZP {...props}>
      <img src={imageProperties.src} width={imageProperties.width} height={imageProperties.height} />
    </MiniMapRZP>
  ) : null;
};

MiniMap.displayName = "MiniMap";

/* -------------------------------------------------------------------------------------------------
 * HOC
 * -----------------------------------------------------------------------------------------------*/

function withReactZoomPanPinchContentRef<P>(
  WrappedComponent: React.ComponentType<P & { rzppRef: ReactZoomPanPinchContentRef }>,
) {
  const ComponentWithReactZoomPanPinchContentRef = (props: P) => {
    return <Root>{ref => <WrappedComponent {...props} rzppRef={ref} />}</Root>;
  };
  return ComponentWithReactZoomPanPinchContentRef;
}

export { ControlButton, Controls, Item, MiniMap, Root, usePlanContext, withReactZoomPanPinchContentRef, Wrapper };
export type { CompassError, ControlButtonProps, ControlsProps, ItemProps, MiniMapProps, RootProps, WrapperProps };
